import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Film, Loader2, Trash2, Play, Clock, Plus, Layers } from 'lucide-react';
import { AppNavbar } from '../components/AppNavbar';
import { MobileTabBar } from '../components/MobileTabBar';

export default function MyReelsPage() {
  const navigate = useNavigate();
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/reels/compositions');
        setComps(res.data.compositions || res.data || []);
      } catch {
        toast.error('Failed to load saved reels');
      }
      setLoading(false);
    })();
  }, []);

  const handleDelete = async (compId) => {
    if (!window.confirm('Delete this reel?')) return;
    try {
      await apiClient.delete(`/reels/compositions/${compId}`);
      setComps(prev => prev.filter(c => c.composition_id !== compId));
      toast.success('Reel deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0" data-testid="my-reels-page">
      <AppNavbar />
      <div className="border-b border-border/50 px-4 py-3 flex items-center gap-3">
        <Film className="h-5 w-5 text-blue-500" />
        <h1 className="text-lg font-semibold">My Reels</h1>
        <div className="flex-1" />
        <Button size="sm" onClick={() => navigate('/reel-editor')} data-testid="create-new-reel-btn">
          <Plus className="h-4 w-4 mr-1" /> Create New Reel
        </Button>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : comps.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Film className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p className="text-lg mb-2">No saved reels yet</p>
            <p className="text-sm mb-4">Create your first reel from a template</p>
            <Button onClick={() => navigate('/reel-editor')}>
              <Plus className="h-4 w-4 mr-1" /> Create Reel
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {comps.map((comp) => {
              const tracks = comp.tracks || [];
              const thumbUrl = tracks[0]?.source_url;
              const totalDur = tracks.reduce((s, t) => s + (t.duration || 3), 0);
              return (
                <div key={comp.composition_id}
                  className="border border-border/50 rounded-xl overflow-hidden bg-card hover:shadow-lg transition-all group cursor-pointer"
                  onClick={() => navigate(`/reel-editor?comp=${comp.composition_id}`)}
                  data-testid={`reel-card-${comp.composition_id}`}>
                  <div className="relative aspect-[9/16] bg-muted/30 max-h-48">
                    {thumbUrl ? (
                      <img src={thumbUrl} alt={comp.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="h-8 w-8 opacity-20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <Play className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-sm truncate mb-1">{comp.name || 'Untitled Reel'}</h3>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {tracks.length} clips</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {totalDur.toFixed(1)}s</span>
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <Button variant="outline" size="sm" className="flex-1 text-xs"
                        onClick={(e) => { e.stopPropagation(); navigate(`/reel-editor?comp=${comp.composition_id}`); }}>
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-xs text-destructive"
                        onClick={(e) => { e.stopPropagation(); handleDelete(comp.composition_id); }}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <MobileTabBar />
    </div>
  );
}
